import * as React from "react";
import { ThemeProvider } from "styled-components";

import Body from "@/components/core-blocks/Body";
import Footer from "@/components/core-blocks/Footer";
import Header from "@/components/core-blocks/Header";
import Main from "@/components/core-blocks/Main";
import Seo, { SEOProps } from "@/components/core-blocks/Seo";
import GlobalStyle from "@/styles/global";
import { IImage, IPageSettings, IPostTypeContext } from "../../types";
import defaultTheme from "@/styles/themes/default.theme";
import { Wysiwyg } from "@/styles/typography";
import S from "./post.styled";
import RelatedPosts from "@/components/RelatedPosts/RelatedPosts";
import ContactInfoCard from "@/components/ContactInfoCard/ContactInfoCard";
import { LocalizationProvider } from "@/contexts/Localization";
import CookieConsent from "@/components/core-blocks/CookieConsent";
import { Helmet } from "react-helmet";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { getCopy } from '@/src/helpers';

interface IPostContext extends IPostTypeContext {
  posts: any;
  content: string;
  title: string;
  date: string;
  customFields: {
    noindex: boolean;
  };
  menuLinks: {
    links: string;
  };
  dynamicMenu: {
    list: string;
  }
  featuredImage?: {
    node?: IImage;
  };
  categories: {
    nodes: [
      {
        name: string;
        posts: {
          nodes: [
            {
              title: string;
              uri: string;
              date: string;
              categories: {
                nodes: [name: string];
              };
            }
          ];
        };
      }
    ];
  };
  contactInfo: {
    image: IImage;
    title: string;
    jobTitle: string;
    mail: string;
    mobile: string;
    name: string;
    text: string;
  };
  seo: SEOProps;
  pageSettings: IPageSettings;
  excerpt: string;
}

export type IPageTemplate = {
  pageContext: IPostContext;
};

const PageTemplate: React.FC<IPageTemplate> = ({
  pageContext: {
    seo,
    content,
    title,
    date,
    featuredImage,
    contactInfo,
    posts,
    ...pageContext
  },
}) => {

  // remove categories with matching title as post title
  const filteredCategories = posts.filter((post: any) => post.title !== title);

  const postImage = featuredImage?.node?.gatsbyImage
    ? getImage(featuredImage.node.gatsbyImage)
    : null;

  const contactImage = contactInfo?.image?.gatsbyImage
    ? getImage(contactInfo.image.gatsbyImage)
    : null;

  const formattedContactInfo = {
    image: contactImage,
    jobTitle: contactInfo.jobTitle,
    mail: contactInfo.mail,
    mobile: contactInfo.mobile,
    name: contactInfo.name,
    text: contactInfo.text,
    title: contactInfo.title,
  };

  const seoContent = {
    title: title.slice(0, 60),
    imageUri: 'https://radargrp.com' + postImage?.images?.fallback?.src,
    lang: pageContext.language.slug,
    date: date,
    description: pageContext?.excerpt ? pageContext?.excerpt.slice(0, 120) : title.slice(0, 120),
    author: contactInfo?.name, meta: []
  };

  return (
    <LocalizationProvider
      language={pageContext.language.slug}
      translations={pageContext.translations}
      uri={pageContext.uri}
    >
      <ThemeProvider theme={defaultTheme}>
        <GlobalStyle />
        <Seo {...seoContent} />
        <Helmet
          htmlAttributes={{ lang: pageContext.language.slug }}
        >
          <title>{title} - Radar</title>
          {pageContext.customFields.noindex && <meta name="robots" content="noindex, nofollow" />}
        </Helmet>
        <Body>
          <Header dynamicMenu={pageContext.dynamicMenu} />
          <Main>
            <S.HeaderBackground />
            <div className="main-inner">
              <S.TitleWrapper>
                {date && <S.Date>{date}</S.Date>}
                {title && <S.Title>{title}</S.Title>}
              </S.TitleWrapper>
              <S.Container>
                <S.Wrapper>
                  {postImage && (
                    <S.Div>
                      <GatsbyImage
                        className="gatsby-image"
                        image={postImage}
                        alt={featuredImage?.node?.altText || ""}
                      />
                      <p>&copy; {getCopy(featuredImage?.node?.altText, featuredImage?.node?.sourceUrl)}{' '}</p>
                    </S.Div>
                  )}
                  {content && (
                    <Wysiwyg
                      color="#000"
                      dangerouslySetInnerHTML={{ __html: content }}
                    />
                  )}
                </S.Wrapper>
                <S.Wrapper>
                  <S.RelatedWrapper>
                    {filteredCategories.length > 0 && (
                      <S.RelatedTitle>Relaterade Inlägg</S.RelatedTitle>
                    )}

                    {filteredCategories &&
                      filteredCategories
                        .slice(0, 5)
                        .map((post: any, key: number) => (
                          <RelatedPosts key={key} post={post} />
                        ))}
                  </S.RelatedWrapper>
                </S.Wrapper>
              </S.Container>
              {contactInfo && (
                <ContactInfoCard contactInfo={formattedContactInfo} />
              )}
            </div>
            <CookieConsent />
          </Main>
          <Footer />
        </Body>
      </ThemeProvider>
    </LocalizationProvider>
  );
};

export default PageTemplate;
